import { useAuth0User } from '@hooks/useAuth0User';
import { hasAdminRole } from '@lib/utils';

/**
 * Non-production builds only. Read from the module rather than inline at the
 * call site so there is exactly one place to check when asking "can this reach
 * production", and so tests can state the production case explicitly.
 */
export const isNonProductionBuild = (): boolean =>
  process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production';

/**
 * Whether the live simulation harness is available to this viewer.
 *
 * Two keys, both required, and both fail closed.
 *
 * The build environment is the hard boundary. It is resolved at build time, so
 * no configuration mistake and no browser-side tampering can surface the
 * harness on production.
 *
 * The `admin` role is the "which humans" boundary. Staging carries real
 * accounts and is not otherwise access-controlled, so the build environment
 * alone would ship this to everyone with a login. The role is issued by Auth0
 * and arrives in the session's roles claim, which makes it a real
 * authorisation signal rather than something the client asserts about itself.
 *
 * A PostHog flag was the obvious candidate for the second key and is the wrong
 * tool for it. Flags are evaluated against a `distinct_id` that only exists
 * after `posthog.identify()`, which `PostHogProvider` gates on analytics
 * consent, so access would depend on a cookie decision that has nothing to do
 * with authorisation. Worse, CookieYes is licensed per domain and does not
 * initialise on `*.vercel.app` at all: consent there resolves denied, PostHog
 * resets to an anonymous id, and every flag falls back to its default. A
 * flag-gated harness is therefore unreachable on exactly the preview
 * deployments a pull request is reviewed on. The roles claim has none of those
 * dependencies: it is on the session at first render, on every domain.
 */
export const useIsSimulationHarnessAvailable = (): boolean => {
  const { user } = useAuth0User();

  return isNonProductionBuild() && hasAdminRole(user);
};
